import { toastError, toastSuccess } from '@/components/toast/toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import {
  archiveTransactions,
  restoreArchivedTransactions
} from '@/api/generated/transaction/transaction'
import {
  TransactionBulkActionRequest,
  TransactionSelectionResult
} from '@/api/generated/model'

export const useArchiveTransactions = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: TransactionBulkActionRequest) => {
      // Called directly against celerity (replaces the /api/v1/transactions/archive BFF route,
      // which fetched the caller's permission pairs and passed them in the body). The endpoint
      // scopes the archive to the caller's own organization and takes the ids alone, like
      // /transactions/validate.
      const response = await archiveTransactions(request)
      return response.data
    },
    onSuccess: (result) => {
      const count = result.Applied
      const skipped = result.Matched - result.Applied
      toastSuccess(
        `Successfully archived ${count} transaction${count === 1 ? '' : 's'}` +
          (skipped > 0 ? `. ${skipped} on a claim were skipped.` : '')
      )

      // Invalidate any transaction-related queries that might need updating
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
    onError: (error) => {
      let message = 'An unexpected error occurred while archiving transactions.'

      if (error instanceof AxiosError) {
        if (
          error.response?.status === 409 &&
          typeof error.response.data?.detail === 'string'
        ) {
          message = error.response.data.detail
        } else if (error.response?.data?.error) {
          message = error.response.data.error
        } else if (error.response?.status === 400) {
          message = 'Invalid request. Please check the selected transactions.'
        } else if (error.response?.status === 403) {
          message = 'You do not have permission to archive these transactions.'
        }
      }

      toastError(message)
    }
  })
}

export const useRestoreTransactions = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: TransactionBulkActionRequest) => {
      const response = await restoreArchivedTransactions(request)
      return response.data
    },
    onSuccess: (result) => {
      const count = result.Applied
      const skipped = result.Matched - result.Applied
      toastSuccess(
        `Successfully restored ${count} transaction${count === 1 ? '' : 's'}` +
          (skipped > 0 ? `. ${skipped} on a claim were skipped.` : '')
      )

      // Invalidate any transaction-related queries that might need updating
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
    onError: (error) => {
      let message = 'An unexpected error occurred while restoring transactions.'

      if (error instanceof AxiosError) {
        if (
          error.response?.status === 409 &&
          typeof error.response.data?.detail === 'string'
        ) {
          message = error.response.data.detail
        } else if (error.response?.data?.error) {
          message = error.response.data.error
        } else if (error.response?.status === 400) {
          message = 'Invalid request. Please check the selected transactions.'
        } else if (error.response?.status === 403) {
          message = 'You do not have permission to restore these transactions.'
        }
      }

      toastError(message)
    }
  })
}
